import { Fragment, startTransition, useEffect, useState } from 'react';

export function useProgressiveCount(total: number, initial = 20, step = 100) {
    const [count, setCount] = useState(initial);

    // Restart from `initial` whenever the list empties (e.g. while a
    // replacement query loads), so the next list ramps up in batches instead
    // of mounting at the old count.
    if (total === 0 && count !== initial) {
        setCount(initial);
    }

    useEffect(() => {
        if (count >= total) {
            return;
        }
        const id = setTimeout(() => {
            startTransition(() => setCount((c) => c + step));
        }, 0);
        return () => clearTimeout(id);
    }, [count, total, step]);

    return Math.min(count, total);
}

/**
 * Renders a list of items in batches, so that there is no single huge paint.
 */
export function ProgressiveList({
    items,
}: {
    /** (key,reactnode) pairs */
    items: [string, React.ReactNode][] | undefined;
}) {
    const count = useProgressiveCount(items?.length ?? 0);
    return (
        <>
            {items?.slice(0, count).map(([key, item]) => (
                <Fragment key={key}>{item}</Fragment>
            ))}
        </>
    );
}
